import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import _ from "lodash";
import { useIntl } from "react-intl";
import WhiteCard from "features/metadesk/importList/components/WhiteCard";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import { useDispatch } from "react-redux";
import { alertServerError } from "features/helpers/utils";
import DatePicker from "@metacrm/metacrm-material-ui/dist/DatePicker";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";

const SmartAlertReportContent = () => {
  const { entityConfig } = useFetchEntityConfig();
  const { formatMessage } = useIntl();
  const [alertType, setAlertType] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedDAO, setSelectedDAO] = useState("");
  const [loading, setLoading] = useState(false);
  const [entityName] = useOutletContext();
  const [alertReport, setAlertReport] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    const defaultData = _.get(entityConfig, "alerts[0]", "");
    setAlertType({
      label: defaultData.name,
      value: defaultData._id,
    });
    if (entityConfig.DAOs?.length > 0) {
      const firstDAO = entityConfig.DAOs[0];
      setSelectedDAO({
        label: firstDAO,
        value: firstDAO,
      });
    }
  }, [entityConfig?.alerts]);

  useEffect(async () => {
    try {
      if (!alertType) return;
      setLoading(true);
      const startDate = dateRange[0];
      const endDate = dateRange[1];
      const params = {
        alertId: alertType.value,
        startDate,
        endDate,
        ...(alertType.label === "DAO's Updates" && {
          DAOName: selectedDAO.value,
        }),
      };

      const result = await axios.get(
        `${apiUrl}/api/notification/performance/${entityName}/alert`,
        { params }
      );

      setAlertReport(result.data);
    } catch (error) {
      dispatch(alertServerError(error));
    } finally {
      setLoading(false);
    }
  }, [alertType, selectedDAO, dateRange]);

  const renderSubscribers = ({ value, tooltip }) => {
    return (
      <Box mt="16px">
        <Box className="startRow">
          <Box mr={"4px"} color="#A5A5A5">
            {formatMessage({ id: "global.function.subscriberCount" })}
          </Box>
          {tooltip && <IconTooltip content={tooltip} />}
        </Box>
        <Box mt="8px">{value}</Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box
        display={"flex"}
        mb="12px"
        alignItems={"flex-end"}
        justifyContent={"space-between"}
        flexWrap={"wrap"}
        gap="12px"
      >
        <Box className="startRow" gap="12px">
          <Box width="360px">
            <AutoCompleteDropdown
              options={_.map(entityConfig?.alerts, (item) => ({
                label: item?.name,
                value: item?._id,
              }))}
              value={alertType}
              onChange={(e) => {
                setAlertType(e.target.value);
              }}
            />
          </Box>
          {alertType?.label === "DAO's Updates" && (
            <Box width="360px">
              <AutoCompleteDropdown
                options={_.map(entityConfig?.DAOs, (item) => ({
                  label: item,
                  value: item,
                }))}
                value={selectedDAO}
                onChange={(e) => {
                  setSelectedDAO(e.target.value);
                }}
              />
            </Box>
          )}
        </Box>

        <DatePicker
          setDateRange={setDateRange}
          dateRange={dateRange}
          allText={formatMessage({ id: "global.filter.all" })}
          datePickerProps={{
            minDate: null,
            maxDate: new Date(),
          }}
          quickSelectOptions={[
            {
              days: 1,
              label: formatMessage({ id: "datePicker.range.day" }),
            },
            {
              days: 7,
              label: formatMessage({ id: "datePicker.range.week" }),
            },
            {
              days: 30,
              label: formatMessage({ id: "datePicker.range.month" }),
            },
            {
              days: "all",
              label: formatMessage({ id: "datePicker.range.all" }),
            },
            {
              days: "custom",
              label: formatMessage({ id: "datePicker.range.custom" }),
            },
          ]}
        />
      </Box>

      <Box mt="16px" pb="16px">
        <Box
          component={Grid}
          container
          spacing={"12px"}
          className="fade-up-more"
        >
          <Grid item xs={12} md={3}>
            <WhiteCard
              isLoading={loading}
              label={formatMessage({
                id: "global.function.widgetImpression",
              })}
              labelTooltip={formatMessage({
                id: "explanation.action.widgetImpression",
              })}
              value={alertReport?.sentData?.widget || "-"}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <WhiteCard
              isLoading={loading}
              label={formatMessage({
                id: "global.function.emailSent",
              })}
              labelTooltip={formatMessage({
                id: "explanation.action.emailSent",
              })}
              value={alertReport?.sentData?.email || "-"}
              width="100%"
              extra={renderSubscribers({
                value: alertReport?.subscribedUsers?.email || "-",
                tooltip: formatMessage({
                  id: "explanation.action.subscriberCount",
                }),
              })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <WhiteCard
              isLoading={loading}
              label={formatMessage({
                id: "global.function.telegramSent",
              })}
              labelTooltip={formatMessage({
                id: "explanation.action.telegramSent",
              })}
              value={alertReport?.sentData?.telegram || "-"}
              width="100%"
              extra={renderSubscribers({
                value: alertReport?.subscribedUsers?.telegram || "-",
              })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <WhiteCard
              isLoading={loading}
              label={formatMessage({
                id: "global.function.discordSent",
              })}
              labelTooltip={formatMessage({
                id: "explanation.action.discordSent",
              })}
              value={alertReport?.sentData?.discord || "-"}
              width="100%"
              extra={renderSubscribers({
                value: alertReport?.subscribedUsers?.discord || "-",
              })}
            />
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default SmartAlertReportContent;
